<script setup lang="ts">
  import GlobalAiChat from '@/components/app/chat/global/GlobalAiChat.vue'
  import GlobalAiChatIntro from '@/components/app/chat/global/GlobalAiChatIntro.vue'
  import { useToast } from '@/components/ui/toast'
  import { useMetrics } from '@/composables/useMetrics'
  import { useTranslation } from '@/composables/useTranslation'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { useUnifiedChatStore } from '@/store/app/unifiedChat'
  import { AIChatSessionTypeEnum, AiChatSessionsService, EventNameEnum } from '@/types/generated'
  import { ApiError } from '@/types/generated/core/ApiError'
  import * as Sentry from '@sentry/vue'
  import { Loader2 } from 'lucide-vue-next'
  import { computed, onMounted, provide, ref } from 'vue'
  import { useRoute } from 'vue-router'

  const route = useRoute()
  const globalChatStore = useGlobalChatStore()
  const unifiedChatStore = useUnifiedChatStore()
  const userStore = useUserStore()
  const { toast } = useToast()
  const { trackEvent } = useMetrics()
  const { t } = useTranslation({
    en: {
      retry: 'Try again',
      expandedSessionGone: 'That chat is no longer available. Starting a new chat.',
      expandedSessionLoadFailed: 'Could not load that chat. Starting a new chat.'
    },
    de: {
      retry: 'Erneut versuchen',
      expandedSessionGone: 'Dieser Chat ist nicht mehr verfügbar. Neuer Chat wird gestartet.',
      expandedSessionLoadFailed:
        'Dieser Chat konnte nicht geladen werden. Neuer Chat wird gestartet.'
    }
  })

  const activeFolderId = ref<string | null>(null)
  const showIntro = ref(false)
  const isLoading = ref(true)

  const activeSessionGroup = computed(() => {
    if (!activeFolderId.value) return null
    return (
      globalChatStore.sessionGroups.find((group) => group.uuid === activeFolderId.value) || null
    )
  })

  provide('sessionType', AIChatSessionTypeEnum.GLOBAL)
  provide('sessionGroupData', activeSessionGroup)

  const handleIntroAccepted = () => {
    showIntro.value = false
  }

  const loadInitialGlobalChatData = async () => {
    await globalChatStore.loadSessionGroups()
    await globalChatStore.loadGlobalPromptTemplates()
    await globalChatStore.loadStandaloneGlobalChatSessions()
    const groupUuid = route.params.groupUuid as string
    if (groupUuid) {
      activeFolderId.value = groupUuid
      await globalChatStore.loadChatSessions(groupUuid)
    }
  }

  const retryInitialLoad = async () => {
    globalChatStore.clearError()
    isLoading.value = true
    try {
      await loadInitialGlobalChatData()
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load the session the user was viewing in the side panel before expanding into fullscreen.
   *
   * The route guard deliberately skips :func:`startNewFullscreenChat` when this path is
   * active (see ``pendingFullscreenSessionUuid`` in :file:`router/index.ts`) so the view has
   * no coherent fresh-chat state to fall back on implicitly. On any failure we must both
   * surface user-visible feedback and restore a usable blank-chat state, otherwise the user
   * lands on the fullscreen view with partially loaded / stale data and no way to recover.
   *
   * - 404 → session was deleted server-side between expand and mount: toast + fresh chat.
   * - Any other error (network, 5xx, etc.) → same recovery, different toast copy so the
   *   user can distinguish "gone" from "try again".
   *
   * :param pendingUuid: UUID of the session to preload on mount.
   * :return: ``None``
   */
  const preloadExpandedFullscreenSession = async (pendingUuid: string): Promise<void> => {
    try {
      const session = await AiChatSessionsService.aiChatSessionsRetrieve({ uuid: pendingUuid })
      globalChatStore.setActiveChatSession(session)
      unifiedChatStore.setActiveSession(session, 'fullscreen')
    } catch (error) {
      const isMissing = error instanceof ApiError && error.status === 404
      toast({
        title: isMissing ? t('expandedSessionGone') : t('expandedSessionLoadFailed'),
        variant: 'destructive'
      })
      console.error('Failed to preload expanded side panel session:', error)
      // 404 is an expected "session deleted server-side" signal — no operational alert
      // needed. Anything else (5xx, network) is a real backend issue worth surfacing.
      if (!isMissing) {
        Sentry.captureException(error, {
          tags: { area: 'ai_assistant_expand_preload' }
        })
      }
      // Recover to a usable fresh-chat state so the view is not left in the partial
      // ``loadInitialGlobalChatData`` state with no active session and no entry path.
      try {
        await unifiedChatStore.startNewFullscreenChat()
      } catch (freshEntryError) {
        console.error('Failed to recover into a fresh fullscreen chat:', freshEntryError)
        Sentry.captureException(freshEntryError, {
          tags: { area: 'ai_assistant_expand_preload_recovery' }
        })
      }
    }
  }

  onMounted(async () => {
    globalChatStore.clearError()
    try {
      if (!userStore.user) {
        await userStore.fetchUser()
      }

      showIntro.value = !(userStore.user as any)?.global_ai_chat_intro_seen

      if (userStore.user && !(userStore.user as any)?.global_ai_chat_badge_dismissed) {
        try {
          const { UsersService } = await import('@/types/generated')
          await UsersService.usersPartialUpdate({
            uuid: userStore.user.uuid!,
            requestBody: {
              global_ai_chat_badge_dismissed: true
            } as any
          })
          if (userStore.user) {
            ;(userStore.user as any).global_ai_chat_badge_dismissed = true
          }
        } catch (error) {
          console.error('Failed to dismiss badge:', error)
        }
      }

      await trackEvent(EventNameEnum.GLOBAL_AI_CHAT_VISITED)

      await loadInitialGlobalChatData()

      // When the user expanded from the floating side panel, preload the same session on the
      // fullscreen view so they land on the exact thread they were viewing. Consumed once so
      // direct ``/app/ai-assistant`` visits still use the fresh-chat entry path.
      const pendingUuid = unifiedChatStore.consumePendingFullscreenSessionUuid()
      if (pendingUuid) {
        await preloadExpandedFullscreenSession(pendingUuid)
      }
    } finally {
      isLoading.value = false
    }
  })
</script>

<template>
  <div class="flex h-[calc(100vh-60px)] w-full">
    <div
      v-if="isLoading"
      class="flex h-full w-full items-center justify-center bg-gray-50"
    >
      <Loader2 class="h-8 w-8 animate-spin text-gray-400" />
    </div>
    <div
      v-else-if="globalChatStore.error"
      class="flex h-full w-full flex-col items-center justify-center gap-4 bg-gray-50 px-6"
      role="alert"
      data-cy="ai-assistant-chat-load-error"
    >
      <p class="max-w-md text-center text-sm text-gray-700">
        {{ globalChatStore.error }}
      </p>
      <button
        type="button"
        class="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
        data-cy="ai-assistant-chat-load-retry"
        @click="retryInitialLoad"
      >
        {{ t('retry') }}
      </button>
    </div>
    <GlobalAiChatIntro
      v-else-if="showIntro"
      @intro-accepted="handleIntroAccepted"
    />
    <GlobalAiChat
      v-else
      :session-group-uuid="activeFolderId || undefined"
      :session-group="activeSessionGroup || undefined"
    />
  </div>
</template>
